export const CUSTOMER_CAPABILITY_IDS = [
  "adu_project_report",
  "project_details",
  "project_facts",
  "project_insights",
  "adu_tasks",
  "adu_project_roadmap",
  "project_generated_items",
  "project_documents",
  "project_artifacts",
  "user_notes",
  "project_decisions",
  "external_sources",
  "report_versions",
] as const;

export const SYSTEM_CAPABILITY_IDS = [
  "capabilities",
  "workspace_artifact",
  "agent_activity",
] as const;

export const DEVELOPMENT_CAPABILITY_COMPONENT_KEYS = [
  "SmartCard",
  "DataTable",
  "SmartForm",
  "Dashboard",
  "MediaViewer",
  "Feed",
  "DetailView",
  "Modal",
  "SplitView",
  "Accordion",
  "TabPanel",
  "GridLayout",
  "Timeline",
  "Checklist",
  "StepperWizard",
  "StateMachine",
  "ProgressTracker",
  "Kanban",
  "ComparisonTable",
  "Calculator",
  "ScenarioSimulator",
  "DecisionTree",
  "BreakdownView",
  "ProjectionChart",
  "RecommendationExplainer",
  "RiskMatrix",
  "CostBuilder",
  "Toast",
  "EmptyState",
  "LoadingState",
  "ErrorState",
] as const;

export type CustomerCapabilityId = (typeof CUSTOMER_CAPABILITY_IDS)[number];
export type SystemCapabilityId = (typeof SYSTEM_CAPABILITY_IDS)[number];
export type DevelopmentCapabilityId = `genui_preview_${(typeof DEVELOPMENT_CAPABILITY_COMPONENT_KEYS)[number]}`;
export type CapabilityId = CustomerCapabilityId | SystemCapabilityId | DevelopmentCapabilityId;

export type CapabilityEntityRef =
  | { kind: "milestone"; id: string; phaseKey?: string }
  | { kind: "artifact"; id: string }
  | { kind: "note"; id: string };

export interface CapabilityNavigationTarget {
  capabilityId: CapabilityId;
  entity?: CapabilityEntityRef;
}

export type CapabilityNavigationHandler = (target: CapabilityNavigationTarget) => void;

export interface CapabilityNavigationOptions {
  allowSystem?: boolean;
  allowDevelopment?: boolean;
}

const CUSTOMER_CAPABILITY_ID_SET = new Set<string>(CUSTOMER_CAPABILITY_IDS);
const SYSTEM_CAPABILITY_ID_SET = new Set<string>(SYSTEM_CAPABILITY_IDS);
const DEVELOPMENT_CAPABILITY_ID_SET = new Set<string>(
  DEVELOPMENT_CAPABILITY_COMPONENT_KEYS.map((key) => `genui_preview_${key}`),
);

const CAPABILITY_ALIASES: Readonly<Record<string, CustomerCapabilityId>> = {
  report: "adu_project_report",
  inputs: "project_details",
  project_saved_items: "project_artifacts",
};

export const ROUTABLE_CAPABILITY_IDS: ReadonlySet<string> = CUSTOMER_CAPABILITY_ID_SET;

function cleanIdentifier(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : null;
}

export function normalizeCapabilityId(
  value: unknown,
  options: CapabilityNavigationOptions = {},
): CapabilityId | null {
  const rawId = cleanIdentifier(value);
  if (!rawId) return null;

  const capabilityId = CAPABILITY_ALIASES[rawId] ?? rawId;
  if (CUSTOMER_CAPABILITY_ID_SET.has(capabilityId)) {
    return capabilityId as CustomerCapabilityId;
  }
  if (options.allowSystem && SYSTEM_CAPABILITY_ID_SET.has(capabilityId)) {
    return capabilityId as SystemCapabilityId;
  }
  if (
    options.allowDevelopment &&
    DEVELOPMENT_CAPABILITY_ID_SET.has(capabilityId)
  ) {
    return capabilityId as DevelopmentCapabilityId;
  }
  return null;
}

function normalizeEntityRef(value: unknown): CapabilityEntityRef | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Record<string, unknown>;
  const id = cleanIdentifier(candidate.id);
  if (!id) return null;

  if (candidate.kind === "milestone") {
    const phaseKey = cleanIdentifier(candidate.phaseKey);
    return phaseKey
      ? { kind: "milestone", id, phaseKey }
      : { kind: "milestone", id };
  }
  if (candidate.kind === "artifact") return { kind: "artifact", id };
  if (candidate.kind === "note") return { kind: "note", id };
  return null;
}

function entityMatchesCapability(
  capabilityId: CapabilityId,
  entity: CapabilityEntityRef,
): boolean {
  if (entity.kind === "milestone") return capabilityId === "adu_tasks";
  if (entity.kind === "note") return capabilityId === "user_notes";
  return capabilityId === "workspace_artifact" ||
    capabilityId === "project_artifacts" ||
    capabilityId === "project_generated_items";
}

/**
 * Normalizes every capability entry boundary, including the two historic
 * CustomEvent payloads (`{ capability }` and `{ capabilityId }`). Callers must
 * emit only the returned canonical shape.
 */
export function normalizeCapabilityNavigationTarget(
  input: unknown,
  options: CapabilityNavigationOptions = {},
): CapabilityNavigationTarget | null {
  const candidate = typeof input === "string"
    ? { capabilityId: input }
    : input && typeof input === "object"
      ? input as Record<string, unknown>
      : null;
  if (!candidate) return null;

  const capabilityId = normalizeCapabilityId(
    candidate.capabilityId ?? candidate.capability,
    options,
  );
  if (!capabilityId) return null;

  if (candidate.entity === undefined) return { capabilityId };
  const entity = normalizeEntityRef(candidate.entity);
  if (!entity || !entityMatchesCapability(capabilityId, entity)) return null;
  return { capabilityId, entity };
}

export function dispatchCapabilityNavigation(
  input: unknown,
  eventTarget: EventTarget = window,
  options: CapabilityNavigationOptions = {},
): CapabilityNavigationTarget | null {
  const target = normalizeCapabilityNavigationTarget(input, options);
  if (!target) return null;
  eventTarget.dispatchEvent(new CustomEvent("orca:openCapability", { detail: target }));
  return target;
}
